import { isEndCustomer, USER_ROLES } from '../../../constants'
import { hasPermission } from '../../../utils/tools'

/**
 * Single source of truth for which order-table columns are visible.
 *
 * The header and the row must agree exactly: the header renders labels in this
 * order and the row renders cells in the same order, so any disagreement shifts
 * every following cell under the wrong label. Both call this helper rather than
 * each applying their own checks.
 */
export const orderColumnIds = [
  'displayId',
  'customerName',
  'connectedReseller',
  'jobName',
  'status',
  'place',
  'createdAt',
  'assignee',
  'preview',
  'channel',
  'budget',
  'price',
  'runtime',
] as const

export type OrderColumnId = (typeof orderColumnIds)[number]

export const isOrderColumnVisible = (
  columnId: OrderColumnId,
  permissionFields: string[] | undefined,
  role: string | undefined
): boolean => {
  const permission = hasPermission(permissionFields)

  // Hard rules, mirroring the field selection in server getOrders.ts: these
  // columns are not returned by the API for these roles, so rendering them
  // would leave a header over an empty cell no matter what the stored
  // permissions say.
  const isCustomerRole =
    role === USER_ROLES.EXTERNAL_DIRECT_CUSTOMER.value ||
    role === USER_ROLES.EXTERNAL_RESELLER_CUSTOMER.value ||
    role === USER_ROLES.EXTERNAL_RESELLER.value
  const isResellerRole = role === USER_ROLES.EXTERNAL_RESELLER.value

  switch (columnId) {
    case 'displayId':
      return true
    case 'customerName':
      return permission('customer') && !isEndCustomer(role)
    case 'connectedReseller':
      return (
        permission('customer') &&
        !isEndCustomer(role) &&
        role !== USER_ROLES.EXTERNAL_RESELLER.value
      )
    case 'preview':
      return true
    case 'status':
      return permission('status') && !isCustomerRole
    case 'budget':
      return permission('budget') && !isCustomerRole
    case 'price':
      return permission('price') && !isResellerRole
    default:
      return permission(columnId)
  }
}

export const getVisibleOrderColumns = (
  permissionFields: string[] | undefined,
  role: string | undefined
): OrderColumnId[] =>
  orderColumnIds.filter((columnId) => isOrderColumnVisible(columnId, permissionFields, role))
